import {
  ICopyDefaultToContractBody,
  IDefaultRulePropagationApplyRequest,
  IDefaultRulePropagationApplyResponse,
  IDefaultRulePropagationPreviewResponse,
  IRulesetListParams,
} from "types/DefaultRules/DefaultRules";
import {
  IGlobalSurgicalPeriodRuleset,
  IGlobalSurgicalPeriodRulesetCreateRequest,
  IGlobalSurgicalPeriodRulesetUpdateRequest,
} from "types/GlobalSurgicalPeriodRuleset/GlobalSurgicalPeriodRuleset";

import { api } from ".";

interface IListResponse {
  data: IGlobalSurgicalPeriodRuleset[];
  message?: string;
}

interface IItemResponse {
  data: IGlobalSurgicalPeriodRuleset;
  message?: string;
}

const globalSurgicalPeriodQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllGlobalSurgicalPeriodRulesets: builder.query<
      IListResponse,
      IRulesetListParams | null | void
    >({
      query: (params) => {
        let search = "";
        if (params?.applicability?.length) {
          search = `?applicability=${params.applicability.join(",")}`;
        } else if (params && params.isDefault !== undefined) {
          search = `?isDefault=${params.isDefault}`;
        }
        return `global-surgical-period-rulesets/all${search}`;
      },
      providesTags: ["GlobalSurgicalPeriodRulesets"],
    }),
    getGlobalSurgicalPeriodRulesetById: builder.query<IItemResponse, string>({
      query: (id) => `global-surgical-period-rulesets/${id}`,
      providesTags: (_r, _e, id) => [
        { type: "GlobalSurgicalPeriodRulesets", id },
      ],
    }),
    createGlobalSurgicalPeriodRuleset: builder.mutation<
      IItemResponse,
      IGlobalSurgicalPeriodRulesetCreateRequest
    >({
      query: (body) => ({
        url: "global-surgical-period-rulesets",
        method: "POST",
        body,
      }),
      invalidatesTags: ["GlobalSurgicalPeriodRulesets"],
    }),
    updateGlobalSurgicalPeriodRuleset: builder.mutation<
      IItemResponse,
      IGlobalSurgicalPeriodRulesetUpdateRequest
    >({
      query: (body) => ({
        url: `global-surgical-period-rulesets/${body.id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [
        "GlobalSurgicalPeriodRulesets",
        "DefaultRulePropagation",
      ],
    }),
    deleteGlobalSurgicalPeriodRuleset: builder.mutation<void, string>({
      query: (id) => ({
        url: `global-surgical-period-rulesets/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "GlobalSurgicalPeriodRulesets",
        "DefaultRulePropagation",
      ],
    }),
    cloneGlobalSurgicalPeriodRulesetAsDefault: builder.mutation<
      IItemResponse,
      string
    >({
      query: (id) => ({
        url: `global-surgical-period-rulesets/${id}/clone-as-default`,
        method: "POST",
      }),
      invalidatesTags: ["GlobalSurgicalPeriodRulesets"],
    }),
    copyGlobalSurgicalPeriodRulesetToContract: builder.mutation<
      IItemResponse,
      { id: string; body: ICopyDefaultToContractBody }
    >({
      query: ({ id, body }) => ({
        url: `global-surgical-period-rulesets/${id}/copy-to-contract`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["GlobalSurgicalPeriodRulesets", "ContractFeeSchedules"],
    }),
    getGlobalSurgicalPeriodPropagationPreview: builder.query<
      IDefaultRulePropagationPreviewResponse,
      string
    >({
      query: (id) => `global-surgical-period-rulesets/${id}/propagate/preview`,
      providesTags: (_r, _e, id) => [
        { type: "DefaultRulePropagation", id: `globalSurgicalPeriod:${id}` },
      ],
    }),
    applyGlobalSurgicalPeriodPropagation: builder.mutation<
      IDefaultRulePropagationApplyResponse,
      { id: string; requests: IDefaultRulePropagationApplyRequest[] }
    >({
      query: ({ id, requests }) => ({
        url: `global-surgical-period-rulesets/${id}/propagate`,
        method: "POST",
        body: { requests },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "GlobalSurgicalPeriodRulesets",
        { type: "DefaultRulePropagation", id: `globalSurgicalPeriod:${id}` },
      ],
    }),
  }),
});

export const {
  useGetAllGlobalSurgicalPeriodRulesetsQuery,
  useGetGlobalSurgicalPeriodRulesetByIdQuery,
  useCreateGlobalSurgicalPeriodRulesetMutation,
  useUpdateGlobalSurgicalPeriodRulesetMutation,
  useDeleteGlobalSurgicalPeriodRulesetMutation,
  useCloneGlobalSurgicalPeriodRulesetAsDefaultMutation,
  useCopyGlobalSurgicalPeriodRulesetToContractMutation,
  useGetGlobalSurgicalPeriodPropagationPreviewQuery,
  useLazyGetGlobalSurgicalPeriodPropagationPreviewQuery,
  useApplyGlobalSurgicalPeriodPropagationMutation,
} = globalSurgicalPeriodQueries;
